import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Building2, CreditCard, Image, LogOut, Loader2, ExternalLink, Star, BarChart3 } from 'lucide-react';
import { useBusinessAuth } from '@/context/BusinessAuthContext';

const LOGO_WORDMARK = "/brand/disqover-wordmark-dark.png";

export default function PortalDashboard() {
  const navigate = useNavigate();
  const { user, loading, business, subscription, logout, refreshUser } = useBusinessAuth();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/partner/login');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    // Refresh user data on mount
    refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/partner');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  // If user has active subscription but no business, redirect to onboarding
  if (user && !business && subscription?.status === 'active') {
    navigate('/portal/onboarding');
    return null;
  }

  // If user has no subscription or subscription is not active, show upgrade prompt
  if (user && (!subscription || subscription?.status !== 'active') && !business) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="portal-no-subscription">
        <header className="bg-white border-b border-slate-100 px-6 py-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <Link to="/">
              <img src={LOGO_WORDMARK} alt="Disqover" className="h-8" />
            </Link>
            <Button variant="ghost" onClick={handleLogout} className="text-slate-600">
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </header>
        
        <main className="px-6 py-12">
          <div className="max-w-md mx-auto text-center">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Building2 className="w-8 h-8 text-teal-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 font-heading mb-4">
              Choose a Plan to Get Started
            </h1>
            <p className="text-slate-600 mb-8">
              Select a subscription plan to list your business on Disqover and start reaching visitors.
            </p>
            <Link to="/partner#pricing">
              <Button className="h-12 px-8 rounded-full bg-teal-600 hover:bg-teal-700">
                View Plans
              </Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portal-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <Link to="/">
              <img src={LOGO_WORDMARK} alt="Disqover" className="h-8" />
            </Link>
            <p className="text-sm text-slate-500 mt-1">Business Portal</p>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600 hidden sm:block">
              {user?.email}
            </span>
            <Button variant="ghost" onClick={handleLogout} className="text-slate-600">
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Sign Out</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-6 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Welcome Section */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-slate-900 font-heading mb-2">
              Welcome, {business?.name || 'Partner'}
            </h1>
            <p className="text-slate-600">
              Manage your listing and subscription from here.
            </p>
          </div>

          {/* Quick Stats */}
          {business && (
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              <div className="bg-white rounded-2xl p-6 border border-slate-100">
                <p className="text-sm text-slate-500 mb-1">Status</p>
                <p className={`text-lg font-semibold ${business.is_active ? 'text-green-600' : 'text-slate-400'}`}>
                  {business.is_active ? 'Live' : 'Inactive'}
                </p>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-slate-100">
                <p className="text-sm text-slate-500 mb-1">Plan</p>
                <div className="flex items-center gap-2">
                  <p className="text-lg font-semibold text-slate-900">Core Listing</p>
                  {business.is_featured && (
                    <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1">
                      <Star className="w-3 h-3" />
                      Boost
                    </span>
                  )}
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-slate-100">
                <p className="text-sm text-slate-500 mb-1">Category</p>
                <p className="text-lg font-semibold text-slate-900 capitalize">
                  {business.category}
                </p>
              </div>
            </div>
          )}

          {/* Menu Cards */}
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/portal/listing">
              <div className="bg-white rounded-2xl p-6 border border-slate-100 hover:border-teal-200 hover:shadow-md transition-all cursor-pointer group">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-teal-200 transition-colors">
                  <Building2 className="w-6 h-6 text-teal-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1">Manage Listing</h3>
                <p className="text-sm text-slate-500">Edit your business details and description</p>
              </div>
            </Link>

            <Link to="/portal/media">
              <div className="bg-white rounded-2xl p-6 border border-slate-100 hover:border-teal-200 hover:shadow-md transition-all cursor-pointer group">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-purple-200 transition-colors">
                  <Image className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1">Media</h3>
                <p className="text-sm text-slate-500">Update your business image</p>
              </div>
            </Link>

            <Link to="/portal/billing">
              <div className="bg-white rounded-2xl p-6 border border-slate-100 hover:border-teal-200 hover:shadow-md transition-all cursor-pointer group">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-amber-200 transition-colors">
                  <CreditCard className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1">Billing</h3>
                <p className="text-sm text-slate-500">View your plan and payment details</p>
              </div>
            </Link>

            <Link to="/portal/analytics">
              <div className="bg-white rounded-2xl p-6 border border-slate-100 hover:border-teal-200 hover:shadow-md transition-all cursor-pointer group">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-green-200 transition-colors">
                  <BarChart3 className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1">Analytics</h3>
                <p className="text-sm text-slate-500">Track views, clicks, and performance</p>
              </div>
            </Link>

            {business && (
              <a 
                href={`${window.location.origin}/business/${business.business_id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="bg-white rounded-2xl p-6 border border-slate-100 hover:border-teal-200 hover:shadow-md transition-all cursor-pointer group">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
                    <ExternalLink className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">View Listing</h3>
                  <p className="text-sm text-slate-500">See how your listing appears to visitors</p>
                </div>
              </a>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
